import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  CrossCloseIcon,
  DeleteIcon,
  DownArrow,
  EditIcon,
  FilterIcon,
  SaveIcon,
} from '@common/utils/icons/icons';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { getOrganizationTreeById } from 'store/organization/organization-actions';
import { selectUser } from 'store/general/general-slice-selectors';
import OrganizationRole from 'models/enums/organization-roles';
import { OrganizationTreeNode } from 'models/organization/list-organization';
import {
  DATE_PRESETS,
  DEFAULT_DATE_PERIOD,
  DatePeriod,
  FleetDashboardFilters,
  getPresetRange,
} from 'models/fleet-management/FleetDataFilter';
import { getCustomerJourneyTagsAsString } from 'models/enums/customer-journey-tag';
import { selectTags, selectTagsRest } from 'store/robots/robots-slice-selectors';
import { getTags } from 'store/robots/robots-actions';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import DateInput from '@common/components/Inputs/DateInput';
import { DateTime } from 'luxon';
import { MILLIS_IN_A_DAY, parseDateString, toDateString } from '@common/utils/date';
import Grid from '@mui/material/Grid';
import { formatFilterPeriod } from 'utils/fleet-filters';
import FilterMultiSelect, { FilterOption } from './FilterMultiSelect';

const DEALER_ROLES: OrganizationRole[] = [
  OrganizationRole.IMPORTER,
  OrganizationRole.WHOLESALER,
  OrganizationRole.DISTRIBUTOR,
];

export enum RobotModel {
  ALL = 'All',
  K900 = 'K900',
  K700 = 'K700',
}

/** Which robots the data is about: only those held at a customer (the default), or every one. */
export enum RobotScope {
  CUSTOMER = 'CUSTOMER',
  ALL = 'ALL',
}

const PERIOD_LABEL_KEYS: Record<DatePeriod, string> = {
  [DatePeriod.CUSTOM]: 'fleet-management-filter-preset-custom',
  [DatePeriod.LAST_4_WEEKS]: 'fleet-management-filter-preset-last-4-weeks',
  [DatePeriod.LAST_12_WEEKS]: 'fleet-management-filter-preset-last-12-weeks',
};

/** One labelled control: the label sits above its input, so every column starts on one line. */
const FilterField: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Stack gap={0.75} minWidth={0}>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    {children}
  </Stack>
);

const FleetFiltersBar: React.FC<{
  filters: FleetDashboardFilters;
  onChange: React.Dispatch<React.SetStateAction<FleetDashboardFilters>>;
  onReset: () => void;
  onSave: (onEdit?: boolean) => void;
  onDelete: () => void;
  isSavedDashboard?: boolean;
  /** Whether the viewer may save, edit and delete dashboards. */
  canSave?: boolean;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
}> = ({
  filters,
  onChange,
  onReset,
  onSave,
  onDelete,
  isSavedDashboard = false,
  canSave = true,
  isEditing,
  onEditingChange,
}) => {
  const { t, i18n } = useTranslation();

  const dispatch = useAppDispatch();

  const currentUser = useAppSelector((state) => selectUser(state));
  const tags = useAppSelector((state) => selectTags(state));
  const tagsRest = useAppSelector((state) => selectTagsRest(state));

  const [organizations, setOrganizations] = useState<OrganizationTreeNode[]>([]);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(true);
  const [hasOrganizationsError, setHasOrganizationsError] = useState(false);

  useEffect(() => {
    if (tagsRest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getTags());
    }
  }, [dispatch, tagsRest.getStatus]);

  useEffect(() => {
    // A slow answer for an earlier organization must not overwrite a newer one.
    let isCurrent = true;
    if (currentUser.data.organization.id === '') return;
    setIsLoadingOrganizations(true);

    dispatch(getOrganizationTreeById(currentUser.data.organization.id))
      .unwrap()
      .then((result) => {
        if (!isCurrent) return;
        setOrganizations(result);
        setHasOrganizationsError(false);
      })
      .catch(() => {
        if (isCurrent) setHasOrganizationsError(true);
      })
      .finally(() => {
        if (isCurrent) setIsLoadingOrganizations(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [dispatch, currentUser.data.organization.id]);

  const dealersOptions = useMemo<OrganizationTreeNode[]>(() => {
    return organizations.filter((org) => DEALER_ROLES.includes(org.role));
  }, [organizations]);

  const childrenByParent = useMemo(() => {
    const map = new Map<string, OrganizationTreeNode[]>();

    organizations.forEach((org) => {
      if (!org.parentId) return;
      const siblings = map.get(org.parentId) ?? [];
      siblings.push(org);
      map.set(org.parentId, siblings);
    });

    return map;
  }, [organizations]);

  const customerIds = useMemo(
    () =>
      new Set(
        organizations.filter((org) => org.role === OrganizationRole.CUSTOMER).map((org) => org.id),
      ),
    [organizations],
  );

  /** A customer, or one of its locations: what the customers filter is allowed to offer. */
  const isCustomerOption = useCallback(
    (org: OrganizationTreeNode) =>
      customerIds.has(org.id) ||
      (org.role === OrganizationRole.LOCATION &&
        org.parentId !== null &&
        customerIds.has(org.parentId)),
    [customerIds],
  );

  const allCustomersOptions = useMemo<OrganizationTreeNode[]>(
    () => organizations.filter(isCustomerOption),
    [isCustomerOption, organizations],
  );

  /**
   * The customers (and their locations) sitting under one of the selected dealers. With no dealer
   * picked nothing is being narrowed, so every customer in reach is offered.
   */
  const customersOptions = useMemo<OrganizationTreeNode[]>(() => {
    if (filters.dealers.length === 0) return allCustomersOptions;

    const seen = new Set<string>(filters.dealers);
    const queue = [...filters.dealers];
    const found: OrganizationTreeNode[] = [];

    while (queue.length > 0) {
      const parent = queue.shift() as string;

      (childrenByParent.get(parent) ?? []).forEach((org) => {
        if (seen.has(org.id)) return;
        seen.add(org.id);
        queue.push(org.id);
        if (isCustomerOption(org)) found.push(org);
      });
    }

    return found;
  }, [allCustomersOptions, childrenByParent, filters.dealers, isCustomerOption]);

  // A customer the new dealer selection no longer holds must not keep narrowing the data.
  useEffect(() => {
    if (isLoadingOrganizations || hasOrganizationsError) return;
    if (filters.dealersCustomers.length === 0) return;

    const allowed = new Set(customersOptions.map((org) => org.id));
    if (filters.dealersCustomers.every((id) => allowed.has(id))) return;

    onChange((previous) => ({
      ...previous,
      dealersCustomers: previous.dealersCustomers.filter((id) => allowed.has(id)),
    }));
  }, [
    customersOptions,
    filters.dealersCustomers,
    hasOrganizationsError,
    isLoadingOrganizations,
    onChange,
  ]);

  const dealersChangeHandler = useCallback(
    (dealers: string[]) => onChange((previous) => ({ ...previous, dealers, dealersCustomers: [] })),
    [onChange],
  );

  const dealersCustomersChangeHandler = useCallback(
    (dealersCustomers: string[]) => onChange((previous) => ({ ...previous, dealersCustomers })),
    [onChange],
  );

  const tagChangeHandler = useCallback(
    (tags: string[]) => onChange((previous) => ({ ...previous, tags })),
    [onChange],
  );

  const countryChangeHandler = useCallback(
    (countries: string[]) => onChange((previous) => ({ ...previous, countries })),
    [onChange],
  );

  const modelChangeHandler = (newValue: RobotModel | null) => {
    if (newValue) onChange({ ...filters, model: newValue });
  };

  const robotScopeChangeHandler = (newValue: RobotScope | null) => {
    if (newValue) onChange({ ...filters, robotScope: newValue });
  };

  /** The presets and the two date inputs drive the same window; "custom" only unpins the presets. */
  const periodChangeHandler = (value: DatePeriod | null) => {
    if (value === null || value === filters.period) return;
    if (value === DatePeriod.CUSTOM) {
      onChange({ ...filters, period: DatePeriod.CUSTOM });
      return;
    }
    onChange({ ...filters, period: value, ...getPresetRange(value) });
  };

  const startDateChangeHandler = (datetime: DateTime | null) => {
    const start = datetime ? toDateString(datetime) : filters.start;
    const end = toDateString(
      DateTime.fromMillis(
        Math.max(
          parseDateString(start).toMillis() + MILLIS_IN_A_DAY,
          parseDateString(filters.end).toMillis(),
        ),
      ),
    );
    onChange({ ...filters, period: DatePeriod.CUSTOM, start, end });
  };

  const endDateChangeHandler = (datetime: DateTime | null) => {
    const end = datetime ? toDateString(datetime) : filters.end;
    const start = toDateString(
      DateTime.fromMillis(
        Math.min(
          parseDateString(end).toMillis() - MILLIS_IN_A_DAY,
          parseDateString(filters.start).toMillis(),
        ),
      ),
    );
    onChange({ ...filters, period: DatePeriod.CUSTOM, start, end });
  };

  const resetClickHandler = () => {
    onReset();
  };

  const editClickHandler = () => {
    onEditingChange(true);
  };

  const saveClickHandler = () => {
    onSave(isEditing);
    onEditingChange(false);
  };

  const dealerFilterOptions = useMemo<FilterOption[]>(
    () =>
      dealersOptions.map((org) => ({
        id: org.id,
        label: org.name,
        caption: t(`organization-role-${org.role}`),
      })),
    [dealersOptions, t],
  );

  const customerFilterOptions = useMemo<FilterOption[]>(
    () => customersOptions.map((org) => ({ id: org.id, label: org.name })),
    [customersOptions],
  );

  const tagOptions = useMemo(
    () =>
      (tags ?? []).map((tag) => ({
        id: tag,
        label: tag,
        group: getCustomerJourneyTagsAsString().includes(tag) ? 'standard' : 'custom',
      })),
    [tags],
  );

  const countryOptions = useMemo(
    () =>
      Array.from(new Set((organizations ?? []).map((org) => org.country)))
        .map((country) => ({
          id: country,
          label: country === '' ? t('fleet-management-filter-no-country') : t(`country-${country}`),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [organizations, t],
  );

  const isInitialState =
    filters.dealers.length === 0 &&
    filters.dealersCustomers.length === 0 &&
    filters.tags.length === 0 &&
    filters.countries.length === 0 &&
    filters.model === RobotModel.ALL &&
    filters.robotScope === RobotScope.CUSTOMER &&
    filters.period === DEFAULT_DATE_PERIOD;

  // A saved dashboard only changes while it is being edited, which only a saver can do.
  const readOnly = isSavedDashboard && !(canSave && isEditing);

  /**
   * What is currently narrowing the data, one chip per filter, so the collapsed bar answers
   * "what am I looking at?" without being read as a sentence. The period is always set, so it
   * closes the row as a plain chip rather than as something to clear.
   */
  const activeFilters = useMemo(() => {
    const summarise = (ids: string[], options: OrganizationTreeNode[], name: string) =>
      ids.length === 1
        ? (options.find((o) => o.id === ids[0])?.name ?? name)
        : `${name}: ${ids.length}`;

    const chips: { key: string; label: string; onDelete?: () => void }[] = [];

    if (filters.dealers.length > 0) {
      chips.push({
        key: 'dealers',
        label: summarise(filters.dealers, dealersOptions, t('fleet-management-filter-dealers')),
        onDelete: readOnly ? undefined : () => onChange({ ...filters, dealers: [] }),
      });
    }
    if (filters.dealersCustomers.length > 0) {
      chips.push({
        key: 'customers',
        label: summarise(
          filters.dealersCustomers,
          customersOptions,
          t('fleet-management-filter-dealers-customers'),
        ),
        onDelete: readOnly ? undefined : () => onChange({ ...filters, dealersCustomers: [] }),
      });
    }
    if (filters.tags.length > 0) {
      chips.push({
        key: 'tags',
        label:
          filters.tags.length === 1
            ? filters.tags[0]
            : `${t('fleet-management-filter-tags')}: ${filters.tags.length}`,
        onDelete: readOnly ? undefined : () => onChange({ ...filters, tags: [] }),
      });
    }
    if (filters.countries.length > 0) {
      chips.push({
        key: 'countries',
        label:
          filters.countries.length === 1
            ? (countryOptions.find((o) => o.id === filters.countries[0])?.label ??
              filters.countries[0])
            : `${t('fleet-management-filter-countries')}: ${filters.countries.length}`,
        onDelete: readOnly ? undefined : () => onChange({ ...filters, countries: [] }),
      });
    }
    if (filters.model !== RobotModel.ALL) {
      chips.push({
        key: 'model',
        label: filters.model,
        onDelete: readOnly ? undefined : () => onChange({ ...filters, model: RobotModel.ALL }),
      });
    }
    if (filters.robotScope === RobotScope.ALL) {
      chips.push({
        key: 'robotScope',
        label: t('fleet-management-filter-robot-scope-all-robots'),
        onDelete: readOnly
          ? undefined
          : () => onChange({ ...filters, robotScope: RobotScope.CUSTOMER }),
      });
    }

    return chips;
  }, [filters, dealersOptions, customersOptions, countryOptions, readOnly, onChange, t]);

  const periodDates = formatFilterPeriod(filters.start, filters.end, i18n.language);
  const period =
    filters.period === DatePeriod.CUSTOM
      ? periodDates
      : `${t(PERIOD_LABEL_KEYS[filters.period])} · ${periodDates}`;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
      <Accordion
        elevation={0}
        disableGutters
        sx={{
          backgroundColor: 'transparent',
          '&:before': { display: 'none' },
          '& .MuiAccordionSummary-content': {
            my: 1.5,
            mr: 2,
            gap: 1.5,
            minWidth: 0,
            alignItems: 'center',
            flexWrap: 'wrap',
          },
        }}
      >
        <AccordionSummary expandIcon={<DownArrow />} sx={{ px: { xs: 2, md: 3 } }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <FilterIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="subtitle1" fontWeight={700}>
              {t('fleet-management-filters')}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            gap={0.75}
            flexWrap="wrap"
            alignItems="center"
            minWidth={0}
            onClick={(event) => event.stopPropagation()}
          >
            {activeFilters.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('fleet-management-filters-none')}
              </Typography>
            )}
            {activeFilters.map((chip) => (
              <Chip key={chip.key} size="small" label={chip.label} onDelete={chip.onDelete} />
            ))}
            <Chip size="small" variant="outlined" label={period} />
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 2, md: 3 }, pt: 0, pb: { xs: 2, md: 3 } }}>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <FilterField label={t('fleet-management-filter-dealers')}>
                <FilterMultiSelect
                  options={dealerFilterOptions}
                  value={filters.dealers}
                  onChange={dealersChangeHandler}
                  disabled={
                    readOnly || (!isLoadingOrganizations && dealerFilterOptions.length === 0)
                  }
                  loading={isLoadingOrganizations}
                  loadingMessage={t('loading-organizations')}
                />
              </FilterField>
            </Grid>

            <Grid item xs={12} md={3}>
              <FilterField label={t('fleet-management-filter-dealers-customers')}>
                <FilterMultiSelect
                  options={customerFilterOptions}
                  value={filters.dealersCustomers}
                  onChange={dealersCustomersChangeHandler}
                  disabled={
                    readOnly || (!isLoadingOrganizations && customerFilterOptions.length === 0)
                  }
                  loading={isLoadingOrganizations}
                  loadingMessage={t('loading-organizations')}
                />
              </FilterField>
            </Grid>

            <Grid item xs={12} md={3}>
              <FilterField label={t('fleet-management-filter-tags')}>
                <FilterMultiSelect
                  options={tagOptions}
                  groups={[
                    { key: 'standard', label: t('fleet-management-filter-tags-standard') },
                    { key: 'custom', label: t('fleet-management-filter-tags-custom') },
                  ]}
                  value={filters.tags}
                  onChange={tagChangeHandler}
                  disabled={readOnly}
                  loading={tagsRest.getStatus === HttpFetchStatus.Loading}
                  loadingMessage={t('loading')}
                />
              </FilterField>
            </Grid>

            <Grid item xs={12} md={3}>
              <FilterField label={t('fleet-management-filter-countries')}>
                <FilterMultiSelect
                  options={countryOptions}
                  value={filters.countries}
                  onChange={countryChangeHandler}
                  disabled={readOnly}
                  loading={isLoadingOrganizations}
                  loadingMessage={t('loading')}
                />
              </FilterField>
            </Grid>

            <Grid item xs={12} md={3}>
              <FilterField label={t('fleet-management-filter-model')}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  value={filters.model}
                  disabled={readOnly}
                  onChange={(_, value) => modelChangeHandler(value as RobotModel | null)}
                  sx={{ flexWrap: 'wrap' }}
                >
                  {Object.values(RobotModel).map((m) => (
                    <ToggleButton key={m} value={m} sx={{ px: 2 }}>
                      {m}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </FilterField>
            </Grid>

            <Grid item xs={12} md={3}>
              <FilterField label={t('fleet-management-filter-robot-scope')}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  value={filters.robotScope}
                  disabled={readOnly}
                  aria-label={t('fleet-management-filter-robot-scope')}
                  onChange={(_, value) => robotScopeChangeHandler(value as RobotScope | null)}
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value={RobotScope.CUSTOMER} sx={{ px: 2 }}>
                    {t('fleet-management-filter-robot-scope-customer')}
                  </ToggleButton>
                  <ToggleButton value={RobotScope.ALL} sx={{ px: 2 }}>
                    {t('fleet-management-filter-option-all')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </FilterField>
            </Grid>

            <Grid item xs={12} md={6}>
              <FilterField label={t('fleet-management-filter-date')}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  gap={2}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  flexWrap="wrap"
                >
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={filters.period}
                    disabled={readOnly}
                    aria-label={t('fleet-management-filter-date')}
                    onChange={(_, value) => periodChangeHandler(value as DatePeriod | null)}
                    sx={{ flexWrap: 'wrap' }}
                  >
                    {[...DATE_PRESETS, DatePeriod.CUSTOM].map((option) => (
                      <ToggleButton key={option} value={option} sx={{ px: 2 }}>
                        {t(PERIOD_LABEL_KEYS[option])}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {/* The presets already say what window they mean, so the two inputs only
                      appear once the window is being picked by hand. */}
                  {filters.period === DatePeriod.CUSTOM && (
                    <Stack direction="row" gap={2} flex={1} minWidth={240} maxWidth={420}>
                      <DateInput
                        date={DateTime.fromISO(filters.start)}
                        onDateTimeChange={startDateChangeHandler}
                        disabled={readOnly}
                        inputTestId="fleet-filter-start-date"
                      />
                      <DateInput
                        date={DateTime.fromISO(filters.end)}
                        onDateTimeChange={endDateChangeHandler}
                        disabled={readOnly}
                        inputTestId="fleet-filter-end-date"
                      />
                    </Stack>
                  )}
                </Stack>
              </FilterField>
            </Grid>
          </Grid>

          {(canSave || (!readOnly && !isInitialState)) && (
            <React.Fragment>
              <Divider sx={{ mt: 3 }} />
              <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap" pt={2}>
                {!readOnly && !isInitialState && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CrossCloseIcon />}
                    onClick={resetClickHandler}
                  >
                    {t('button-reset')}
                  </Button>
                )}
                {canSave && !readOnly && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={saveClickHandler}
                  >
                    {t('button-save')}
                  </Button>
                )}
                {canSave && readOnly && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DeleteIcon />}
                    color="error"
                    onClick={onDelete}
                  >
                    {t('button-delete')}
                  </Button>
                )}
                {canSave && readOnly && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={editClickHandler}
                  >
                    {t('button-edit')}
                  </Button>
                )}
              </Stack>
            </React.Fragment>
          )}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default FleetFiltersBar;
